/**
 * The structure of a letter, read from its text and written back to it.
 *
 * Every notice and report is plain text under one grammar (a title, meta
 * lines "Key: value", roman headings "I. Scope", sub headings, bullets
 * "- item", detail lines indented two spaces, pipe tables, a signature).
 * parseLetterBlocks reads that text into blocks; serializeLetterBlocks
 * writes blocks back as the same text, so a letter edited block by block on
 * the screen (operator order 2026-10-16: the report renders as the letter
 * and is edited as the letter) is saved as the text every renderer takes.
 *
 * Pure: no I/O, no imports. The client keeps a verbatim copy
 * (src/pages/Audit/letterBlocks.js); a test holds the two identical.
 */

const ROMAN_RE = /^([IVX]+)\.\s+(.+)$/;
const BULLET_RE = /^\s*[-•]\s+(.+)$/;
const META_RE = /^([A-Z][A-Za-z /&]{1,40}):\s+(.+)$/;
const TITLE_RE = /^(RE:|DRAFT AUDIT REPORT|FINAL AUDIT REPORT|AUDIT REPORT)/;
const TABLE_ROW_RE = /^\|.*\|$/;
const CONDITION_RE = /^(Condition|Observation|Factor) (\d+|[ivx]+)(\.[a-z]+)?: /;

/**
 * Blocks: title, meta, heading, subheading, paragraph, bullets, detail,
 * table, signature, blank. Conservative on purpose: anything it cannot
 * classify is a paragraph, so no line is ever dropped or reordered.
 */
export function parseLetterBlocks(text) {
  const lines = String(text || '').replace(/\r\n?/g, '\n').split('\n');
  const blocks = [];
  // Trailing signature: the last run of non-blank lines when they are short
  // and unpunctuated (an organization name and a unit), never sentences.
  let sigStart = lines.length;
  {
    let i = lines.length - 1;
    while (i >= 0 && !lines[i].trim()) i--;
    const end = i;
    while (i >= 0 && lines[i].trim() && lines[i].trim().length <= 60 && !/[.:;?]$/.test(lines[i].trim()) && !BULLET_RE.test(lines[i])) i--;
    if (end - i >= 1 && end - i <= 4 && i >= 0 && !lines[i].trim()) sigStart = i + 1;
  }
  let inHead = true; // the RE block at the top: title line plus its meta lines
  let i = 0;
  while (i < lines.length) {
    const raw = lines[i];
    const line = raw.trimEnd();
    const t = line.trim();
    if (i >= sigStart) {
      const sig = [];
      while (i < lines.length) { if (lines[i].trim()) sig.push(lines[i].trim()); i++; }
      if (sig.length) blocks.push({ type: 'signature', lines: sig });
      break;
    }
    if (!t) { inHead = false; if (blocks.length && blocks[blocks.length - 1].type !== 'blank') blocks.push({ type: 'blank' }); i++; continue; }
    if (inHead) {
      if (blocks.length === 0 || (blocks.length === 1 && blocks[0].type === 'title' && /^[A-Z ]{6,}$/.test(blocks[0].text) && !META_RE.test(t))) {
        blocks.push({ type: 'title', text: t, kicker: blocks.length === 1 ? blocks[0].text : null });
        if (blocks.length === 2) blocks.splice(0, 1);
        i++; continue;
      }
      const m = t.match(META_RE);
      if (m) { blocks.push({ type: 'meta', key: m[1], value: m[2] }); i++; continue; }
      inHead = false;
    }
    if (BULLET_RE.test(line)) {
      const items = [];
      while (i < lines.length && BULLET_RE.test(lines[i])) { items.push(lines[i].match(BULLET_RE)[1].trim()); i++; }
      blocks.push({ type: 'bullets', items });
      continue;
    }
    // A table: consecutive pipe rows, the first row is the header (the CMS
    // format's summary of results, factor rates, county scores, actions).
    if (TABLE_ROW_RE.test(t)) {
      const rows = [];
      while (i < lines.length && TABLE_ROW_RE.test(lines[i].trim())) {
        rows.push(lines[i].trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim()));
        i++;
      }
      blocks.push({ type: 'table', header: rows[0], rows: rows.slice(1) });
      continue;
    }
    const rm = t.match(ROMAN_RE);
    if (rm && t.length <= 140) { blocks.push({ type: 'heading', numeral: rm[1], text: rm[2].trim() }); i++; continue; }
    // A condition of the audit (CMS format) is a sub-heading whatever its
    // punctuation, so each condition starts its own block on the page.
    if (CONDITION_RE.test(t) && t.length <= 160) { blocks.push({ type: 'subheading', text: t }); i++; continue; }
    if (/^\s{2,}\S/.test(raw)) {
      const dm = t.match(/^([A-Z][A-Za-z ()]{2,40}):\s+(.*)$/);
      blocks.push(dm ? { type: 'detail', key: dm[1], text: dm[2] } : { type: 'detail', key: null, text: t });
      i++; continue;
    }
    // A sub-heading: short, no sentence punctuation, and the next line goes
    // on with content (a list, a detail line, a paragraph).
    const next = lines[i + 1] || '';
    if (t.length <= 90 && !/[.:;?!]$/.test(t) && !/:\s/.test(t) && !/^(Dear|To the)\b/.test(t) && next.trim() && !ROMAN_RE.test(next.trim()) && blocks.length && !TITLE_RE.test(t)) {
      blocks.push({ type: 'subheading', text: t }); i++; continue;
    }
    if (TITLE_RE.test(t) && blocks.length && blocks.every((b) => b.type !== 'title')) { blocks.push({ type: 'title', text: t }); i++; continue; }
    blocks.push({ type: 'paragraph', text: t });
    i++;
  }
  while (blocks.length && blocks[blocks.length - 1].type === 'blank') blocks.pop();
  return blocks;
}

/**
 * Blocks back to the text the renderers take. The inverse of the parse for
 * every letter the builders write: parse(serialize(parse(t))) is parse(t).
 */
export function serializeLetterBlocks(blocks) {
  const out = [];
  for (const b of blocks || []) {
    switch (b.type) {
      case 'title': if (b.kicker) out.push(b.kicker); out.push(b.text); break;
      case 'meta': out.push(`${b.key}: ${b.value}`); break;
      case 'blank': out.push(''); break;
      case 'heading': out.push(`${b.numeral}. ${b.text}`); break;
      case 'subheading': out.push(b.text); break;
      case 'bullets': for (const it of b.items) out.push(`- ${it}`); break;
      case 'detail': out.push(`  ${b.key ? `${b.key}: ` : ''}${b.text}`); break;
      case 'paragraph': out.push(b.text); break;
      case 'table': out.push(`| ${b.header.join(' | ')} |`); for (const r of b.rows) out.push(`| ${r.join(' | ')} |`); break;
      case 'signature': if (out.length && out[out.length - 1] !== '') out.push(''); for (const l of b.lines) out.push(l); break;
      default: break;
    }
  }
  return out.join('\n').replace(/\s+$/, '');
}
